import React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Modal } from '@mui/material';

/** Server-side role-suppression preview state. See api/viewAs.ts. */
interface ViewAsState {
  active: boolean;
  suppressedRoles: string[];
  availableRoles: string[];
}

interface UserInfo {
  oid: string;
  tid?: string;
  accountKey?: string;
  /**
   * Opaque digest of this account and its role preview, written to a readable cookie by the same
   * response. See Features/AccountMarker.cs.
   */
  accountMarker?: string;
  multiTenantEnabled?: boolean;
  name: string;
  email: string;
  /** Roles in effect right now — already has any view-as suppression applied by the server. */
  roles: string[];
  /** Write restriction remains active even when View as hides the audit role. */
  accessibilityAuditSession?: boolean;
  viewAs?: ViewAsState;
}

interface AuthContextValue {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  hasRole: () => false,
  hasAnyRole: () => false,
  logout: () => {},
});

export const useAuth = () => React.useContext(AuthContext);

function redirectToLogin(): void {
  const returnUrl = window.location.pathname + window.location.search + window.location.hash;
  window.location.href = '/api/auth/login?returnUrl=' + encodeURIComponent(returnUrl);
}

function getAccountKey(user: UserInfo | null): string {
  return user?.accountKey ?? (user ? `${user.tid ?? ''}/${user.oid}` : 'anonymous');
}

/**
 * Identity for caches whose contents depend on what the user may do.
 *
 * <p>Deliberately different from {@link getAccountKey}, which ignores roles so that a role
 * refresh does not remount the whole tree. A permission-scoped cache has the opposite
 * requirement: it must notice a role change, so the roles are folded in here.</p>
 *
 * <p>Sorted, because the server does not promise a stable order and a reordering is not an
 * identity change. Returned as a primitive so consumers can use it as an effect dependency —
 * `/api/auth/user` returns a fresh object on every reverification, so depending on the user
 * object itself makes an effect fire on every tab switch.</p>
 */
export function getAccessIdentityKey(user: UserInfo | null): string {
  return user ? `${getAccountKey(user)}|${[...user.roles].sort().join(',')}${user.accessibilityAuditSession ? '|audit-session' : ''}` : 'anonymous';
}

/**
 * The readable companion to the HttpOnly auth cookie, or null when it is absent. The value is
 * base64url, so it is compared exactly as stored — decoding it would only add a way to throw on a
 * malformed cookie, and throwing here would strand the tree inert instead of falling through to
 * the full check.
 */
function readAccountMarker(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)AskIAM\.Account=([^;]*)/);
  return match ? match[1] : null;
}

/**
 * How long a matching marker may stand in for a real check. The marker already changes on every
 * event we know about, so this only bounds the ones we cannot see — chiefly a response that raced
 * a change in another tab and wrote back the state it had read a moment earlier. It turns any such
 * window from open-ended into a few minutes, at a cost of roughly one request per five idle
 * minutes rather than one per tab switch.
 */
const MARKER_MAX_AGE_MS = 5 * 60_000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const reverifyInFlightRef = React.useRef(false);
  const userRef = React.useRef<UserInfo | null>(null);
  const reverifyControllerRef = React.useRef<AbortController | null>(null);
  // Monotonic, so a backward wall-clock jump cannot make the elapsed time negative and hold the
  // marker "fresh" past its bound. Starts unreachably stale so nothing is skipped before the first
  // successful verification.
  const verifiedAtRef = React.useRef(Number.NEGATIVE_INFINITY);
  const [reverification, setReverification] = React.useState<'ready' | 'away' | 'checking' | 'failed'>('checking');

  const reverify = React.useCallback(async () => {
    if (reverifyInFlightRef.current) return;
    reverifyInFlightRef.current = true;
    setReverification('checking');
    const controller = new AbortController();
    reverifyControllerRef.current = controller;
    try {
      const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)]);
      const res = await fetch('/api/auth/user', { credentials: 'same-origin', signal });
      if (controller.signal.aborted) return;
      if (res.status === 401 || res.status === 403) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`Account verification failed (${res.status}).`);
      const data: UserInfo = await res.json();
      if (controller.signal.aborted) return;
      if (userRef.current && getAccountKey(data) !== getAccountKey(userRef.current)) {
        // A remount alone does not clear module-level API caches from the old account.
        window.location.reload();
        return;
      }
      userRef.current = data;
      verifiedAtRef.current = performance.now();
      setUser(data);
      setIsLoading(false);
      setReverification(document.visibilityState === 'visible' && document.hasFocus() ? 'ready' : 'away');
    } catch {
      if (!controller.signal.aborted) setReverification('failed');
    } finally {
      if (!controller.signal.aborted) {
        reverifyInFlightRef.current = false;
        reverifyControllerRef.current = null;
      }
    }
  }, []);

  // Block before leaving the tab/window; its shared cookie can change while it is away.
  React.useEffect(() => {
    const onBlur = () => {
      setReverification('away');
      reverifyControllerRef.current?.abort();
      reverifyControllerRef.current = null;
      reverifyInFlightRef.current = false;
    };
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      // The server rewrites or removes the marker on every event that changes who the caller is —
      // sign-in, account switch, view-as, sign-out — and it expires with the ticket it describes.
      // So a marker that still matches the value returned with our own profile, recently enough,
      // means a round trip has nothing to tell us. Skipping it is what keeps a five-second alt-tab
      // free of a blocking check. Every other case falls through to the full verification below,
      // so this can only remove redundant work, never a needed check.
      const marker = userRef.current?.accountMarker;
      const fresh = performance.now() - verifiedAtRef.current < MARKER_MAX_AGE_MS;
      if (!reverifyInFlightRef.current && marker && fresh && readAccountMarker() === marker) {
        setReverification('ready');
        return;
      }
      void reverify();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
      else onBlur();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    void reverify();
    return () => {
      reverifyControllerRef.current?.abort();
      reverifyInFlightRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [reverify]);

  const logout = React.useCallback(() => {
    window.location.href = '/api/auth/logout';
  }, []);

  const hasRole = React.useCallback(
    (role: string) => user?.roles?.includes(role) || user?.roles?.includes('AskIAM.Admin') || false,
    [user],
  );

  const hasAnyRole = React.useCallback(
    (roles: string[]) => roles.some(r => user?.roles?.includes(r)) || user?.roles?.includes('AskIAM.Admin') || false,
    [user],
  );

  const value = React.useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      hasRole,
      hasAnyRole,
      logout,
    }),
    [user, isLoading, hasRole, hasAnyRole, logout],
  );

  // A different directory account must not inherit mounted forms or cached personal state.
  // Role refreshes (including view-as) intentionally do not change this key.
  const accountKey = getAccountKey(user);
  const showVerificationDialog = reverification === 'failed' || (isLoading && reverification === 'checking');
  const backgroundCheck = !isLoading && (reverification === 'away' || reverification === 'checking');
  return (
    <AuthContext.Provider value={value}>
      <div inert={reverification !== 'ready'} style={{ display: 'contents' }}>
        <React.Fragment key={accountKey}>{children}</React.Fragment>
      </div>
      {/* Portaled controls escape inert. Keep default focus trapping and restoration so keyboard
          actions cannot reach them until the account is verified. */}
      <Modal
        open={backgroundCheck}
        hideBackdrop disableScrollLock disableEscapeKeyDown
        sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1 }}
      >
        <Box tabIndex={-1} role="status" aria-label="Verifying your account" sx={{ outline: 0 }}>
          {reverification === 'checking' && (
            <LinearProgress
              aria-label="Verifying your account"
              sx={{ position: 'fixed', top: 0, left: 0, right: 0 }}
            />
          )}
        </Box>
      </Modal>
      <Dialog open={showVerificationDialog} disableEscapeKeyDown aria-labelledby="account-verification-title">
        <DialogTitle id="account-verification-title">
          {reverification === 'failed' ? 'Could not verify your account' : 'Checking your account'}
        </DialogTitle>
        <DialogContent>
          {reverification === 'failed'
            ? 'Your page is paused until your account can be verified. Please try again.'
            : <CircularProgress aria-label="Verifying your account" />}
        </DialogContent>
        {reverification === 'failed' && (
          <DialogActions><Button onClick={() => void reverify()}>Retry</Button></DialogActions>
        )}
      </Dialog>
    </AuthContext.Provider>
  );
};

export default AuthContext;
